import { z } from 'zod';
import {
  RASPIL_CATEGORY,
  RASPIL_SUBCATEGORIES,
  type RaspilSubcategory,
} from '../domain/partner-categories';
import { PaginatedResponseSchema } from './pagination';
import { CitySchema, UserRegionSchema } from './onboarding';

// ── serviceSubcategory: enum распил-подкатегорий из словаря Блока 8 (ADR-0086 amendment) ──
// Блок 8 экспортирует const+type (НЕ Zod-schema) → строим enum локально, паттерн onboarding.ts
// (cityValues = CITIES as unknown as [City, ...City[]]; CitySchema = z.enum(cityValues)).
const raspilSubValues = RASPIL_SUBCATEGORIES as unknown as [
  RaspilSubcategory,
  ...RaspilSubcategory[],
];
const RaspilSubcategorySchema = z.enum(raspilSubValues);

/**
 * Блок 9 «Сотрудничество» — Zod-контракты заявки поставщика. ADR-0086, PR#2 задача 0.3.
 *
 * Три формы (как у `feedback.ts`): input (публичный apply) / stored (региональный
 * Postgres, P-007) / admin-view (read-shape Админки — Блок 19, Фаза 3). В этом
 * контракте — ТОЛЬКО форма. Эндпоинт `POST /api/cooperation/apply` + Turnstile +
 * rate-limit + PDF→R2 — задача 2.2 (Фаза 2); таблица `cooperation_applications`
 * (region-db, файл `cooperation.ts` по C7) — Фаза 1; admin read-эндпоинт — Фаза 3.
 *
 * ── Инвариант §1a (анти-спуфинг, P-009) ─────────────────────────────────────
 * Эндпоинт ПУБЛИЧНЫЙ (без JWT — регистрация новых поставщиков), поэтому в
 * `CooperationApplyInputSchema` НЕТ серверных полей `userId`/`region`/`ts`:
 *   - `region` сервер выводит из города: `region = CITY_REGION_MAP[city]` (НЕ из
 *     токена — токена нет; из города, как клиентский authStore). От этого зависит,
 *     в какую региональную базу ляжет заявка.
 *   - `ownerUserId` = null на момент подачи (аноним); проставится при одобрении (C3).
 * Лишние поля из тела клиента молча срезаются (default strip ZodObject) — сервер
 * их игнорирует и перезаписывает авторитетными значениями (ADR §1a, паттерн analytics).
 *
 * ── service (§2 ADR, R1) ─────────────────────────────────────────────────────
 * `service` = каноничная «Услуга» Блока 8 ИЛИ `'other'` (free-text «Другое», PRD
 * 2.9.1). Словаря услуг Блока 8 в `packages/shared/src/domain/` ПОКА НЕТ (Блок 8 —
 * отдельный поток; категория партнёра — free-text `z.string()` в `contracts/partners.ts`),
 * поэтому здесь `z.string().min(1)` verbatim (P-001). TODO: заменить на enum/ссылку,
 * когда появится словарь Блока 8 — НЕ выдумывать перечень (анти-додумывание, CLAUDE.md).
 * `customService` обязателен ⟺ `service === 'other'` (superRefine, паттерн feedback.customTag).
 *
 * ── ≥1 мессенджер (§3 ADR, РЕШЕНО Aibek 2026-06-15) ──────────────────────────
 * whatsapp/telegram/max опциональны по отдельности, но хотя бы один из трёх
 * обязателен (superRefine) — иначе с поставщиком не связаться. issue → на `whatsapp`.
 *
 * `pdfR2Key` — verbatim ключ R2 (P-001, без regex/transform). Безопасность:
 * эндпоинт публичный → ключ ДОЛЖЕН быть выдан сервером (signed-upload) и проверяться
 * на принадлежность заявке — реализация в 2.2 (контракт не меняем).
 *
 * References:
 *   - ADR-0086 §1/§1a/§2/§3, §Недостатки (pdfR2Key security флаг для 2.2)
 *   - PRD `docs/prd/v4/13-block-9-cooperation.md`, `01-foundation.md` §1.7
 *   - паттерн 3-форм + customTag-refine: `contracts/feedback.ts`
 *   - паттерн input/stored анти-спуфинг: `contracts/analytics.ts`
 *   - `CITIES`/`CITY_REGION_MAP`: `domain/onboarding-vocabularies.ts` (ADR-0085, 20 городов)
 *   - policies: P-001, P-007, P-009, P-011, P-012, P-013, P-016
 */

// ── Статус модерации (Блок 19); канон — `new` (ADR §Контекст; `pending` не используем) ──
export const CooperationStatusSchema = z.enum(['new', 'approved', 'rejected']);
export type CooperationStatus = z.infer<typeof CooperationStatusSchema>;

// ── 1a. Форма входа клиента (публичный apply) — БЕЗ серверных полей (§1a) ──────
// ZodObject (не .superRefine) — нужен чистый object для `.extend()` хранимой формы
// (паттерн feedback: refinement вынесен в named function, применяется в двух местах).
const cooperationInputObject = z.object({
  // service: каноничная «Услуга» Блока 8 ИЛИ 'other'. R1: словаря Блока 8 в shared
  // ПОКА нет → z.string().min(1) verbatim (P-001), TODO выше. Не выдумывать перечень.
  service: z.string().min(1),
  // обязателен ⟺ service==='other' (superRefine ниже)
  customService: z.string().min(1).max(200).optional(),
  // подкатегория распила (ADR-0086 amendment): обязательна ⟺ service===RASPIL_CATEGORY,
  // запрещена иначе (superRefine ниже). z.enum(RASPIL_SUBCATEGORIES) из словаря Блока 8 (не free-string).
  serviceSubcategory: RaspilSubcategorySchema.optional(),
  // НЕ free-string: иначе CITY_REGION_MAP[city] === undefined на сервере (region вывод)
  city: CitySchema,
  companyOrName: z.string().min(1).max(200),
  // контактное лицо для компании (опционально для ИП/физлица)
  contactPerson: z.string().max(200).optional(),
  address: z.string().min(1).max(300),
  whatsapp: z.string().max(100).optional(),
  telegram: z.string().max(100).optional(),
  max: z.string().max(100).optional(),
  message: z.string().min(1).max(2000),
  // verbatim ключ R2 (P-001); загрузка — отдельный multipart (2.2); серверный ключ (signed-upload)
  pdfR2Key: z.string().min(1).max(500).optional(),
});

type CooperationInputData = z.infer<typeof cooperationInputObject>;

/**
 * superRefine (2 правила, ADR §2+§3), применяется к input И к stored:
 *   (1) customService обязателен ⟺ service==='other' (паттерн feedback.customTag);
 *   (2) хотя бы один из whatsapp/telegram/max задан — иначе issue на whatsapp.
 */
const cooperationRefinement = (data: CooperationInputData, ctx: z.RefinementCtx): void => {
  if (data.service === 'other' && !data.customService) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'customService обязателен при service === "other"',
      path: ['customService'],
    });
  }
  if (data.service !== 'other' && data.customService) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'customService допустим только при service === "other"',
      path: ['customService'],
    });
  }
  // serviceSubcategory обязательна ⟺ service === RASPIL_CATEGORY (ADR-0086 amendment)
  if (data.service === RASPIL_CATEGORY && !data.serviceSubcategory) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Для распила укажите подкатегорию',
      path: ['serviceSubcategory'],
    });
  }
  if (data.service !== RASPIL_CATEGORY && data.serviceSubcategory) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Подкатегория допустима только для распила',
      path: ['serviceSubcategory'],
    });
  }
  if (!data.whatsapp && !data.telegram && !data.max) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Укажите хотя бы один мессенджер: WhatsApp, Telegram или Max',
      path: ['whatsapp'],
    });
  }
};

export const CooperationApplyInputSchema =
  cooperationInputObject.superRefine(cooperationRefinement);
export type CooperationApplyInput = z.infer<typeof CooperationApplyInputSchema>;

// ── 1b. Хранимая запись (региональный Postgres, P-007) = Input + серверный конверт ──
// Серверные поля проставляет эндпоинт/региональный writer (region из города, не из тела).
const cooperationApplicationObject = cooperationInputObject.extend({
  id: z.string().uuid(),
  // null до одобрения (аноним); uuid после привязки к пользователю (C3)
  ownerUserId: z.string().uuid().nullable(),
  // СЕРВЕР выводит из города: region = CITY_REGION_MAP[city] (публичный эндпоинт, не JWT)
  region: UserRegionSchema,
  status: CooperationStatusSchema,
  // канал/метка источника заявки
  source: z.string(),
  createdAt: z.string().datetime(),
});

export const CooperationApplicationSchema =
  cooperationApplicationObject.superRefine(cooperationRefinement);
export type CooperationApplication = z.infer<typeof CooperationApplicationSchema>;

// ── 1c. Read-shape Админки (Блок 19, Фаза 3) — проекция хранимой записи ────────
// Админ видит все поля заявки + status + createdAt. Форма фиксируется здесь;
// эндпоинт `GET /api/admin/cooperation-applications` (admin-JWT/TOTP, P-016) — Фаза 3.
// Толерантный read-object (без refinement): инварианты гарантированы при записи.
export const CooperationApplicationAdminViewSchema = cooperationApplicationObject;
export type CooperationApplicationAdminView = z.infer<typeof CooperationApplicationAdminViewSchema>;

// Список заявок Админки — канонная пагинированная обёртка (как catalog-списки).
export const CooperationApplicationListSchema = PaginatedResponseSchema(
  CooperationApplicationAdminViewSchema,
);
export type CooperationApplicationList = z.infer<typeof CooperationApplicationListSchema>;

// ── 1d. Ответ публичного apply — { id, status:new } (паритет FeedbackCreateResponse) ──
export const CooperationApplyResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.literal('new'),
});
export type CooperationApplyResponse = z.infer<typeof CooperationApplyResponseSchema>;
